// src/pages/GestionarUsuarios.jsx
import { useState, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useUsuarios } from "../hooks/useUsuarios";
import { ROLES_CONFIG } from "../constants";
import { Select } from "../components/common/Select";
import { Input } from "../components/common/Input";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { Card } from "../components/common/Card";
import { Alert } from "../components/common/Alert";
import { Loading } from "../components/common/Loading";

export default function GestionarUsuarios() {
  const { isAdmin } = useAuth();
  const { usuarios, loading, updateRole, toggleStatus, error } = useUsuarios();
  
  const [filters, setFilters] = useState({
    role: "",
    activo: "",
    search: ""
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Filtrar usuarios
  const filteredUsuarios = useMemo(() => {
    return usuarios.filter(usuario => {
      const matchRole = !filters.role || usuario.role === filters.role;
      const matchActivo = !filters.activo || 
        (filters.activo === "activo" ? usuario.activo : !usuario.activo);
      const matchSearch = !filters.search ||
        usuario.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        (usuario.nombre && usuario.nombre.toLowerCase().includes(filters.search.toLowerCase()));
      
      return matchRole && matchActivo && matchSearch;
    });
  }, [usuarios, filters]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateRole(userId, newRole);
      setSuccessMessage("Rol actualizado correctamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(err.message || "Error al actualizar el rol");
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
  const action = currentStatus ? "desactivar" : "activar";
  const actionText = currentStatus ? "desactivado" : "activado";
  
  if (!window.confirm(`¿Estás seguro de ${action} este usuario?`)) return;

  try {
    await toggleStatus(userId, currentStatus);
    setSuccessMessage(`Usuario ${actionText} correctamente`);
    setTimeout(() => setSuccessMessage(""), 3000);
  } catch (err) {
    setErrorMessage(err.message || `Error al ${action} el usuario`);
  }
};

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return "N/A";
    try {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric"
      };
      if (includeTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
      }
      return new Date(dateString).toLocaleDateString("es-AR", options);
    } catch {
      return "N/A";
    }
  };

  if (!isAdmin) {
    return (
      <Alert variant="error">
        Solo los administradores pueden acceder a esta sección
      </Alert>
    );
  }

  if (loading) {
    return <Loading text="Cargando usuarios..." />;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{
          fontSize: "32px",
          fontWeight: "700",
          color: "#1e293b",
          margin: "0 0 8px 0"
        }}>
          Gestionar Usuarios
        </h1>
        <p style={{
          fontSize: "16px",
          color: "#64748b",
          margin: 0
        }}>
          Total: {filteredUsuarios.length} usuario{filteredUsuarios.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Mensajes */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      
      {(errorMessage || error) && (
        <Alert variant="error" onClose={() => setErrorMessage("")}>
          {errorMessage || error}
        </Alert>
      )}

      {/* Filtros */}
      <Card padding="24px" style={{ marginBottom: "24px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px"
        }}>
          <Select
            label="Rol"
            value={filters.role}
            onChange={(e) => setFilters({...filters, role: e.target.value})}
            options={[
              { value: "", label: "Todos los roles" },
              ...ROLES_CONFIG.map(r => ({ value: r.value, label: r.label }))
            ]}
          />
          
          <Select
            label="Estado"
            value={filters.activo}
            onChange={(e) => setFilters({...filters, activo: e.target.value})}
            options={[
              { value: "", label: "Todos los estados" },
              { value: "activo", label: "Activos" },
              { value: "inactivo", label: "Inactivos" }
            ]}
          />
          
          <Input
            label="Buscar"
            placeholder="Email o nombre..."
            icon="🔍"
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
        </div>
      </Card>

      {/* Estadísticas rápidas */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "16px",
        marginBottom: "24px"
      }}>
        {ROLES_CONFIG.map(role => {
          const count = usuarios.filter(u => u.role === role.value).length;
          return (
            <Card key={role.value} padding="16px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#00a680",
                  marginBottom: "4px"
                }}>
                  {count}
                </div>
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  {role.label}
                </div>
              </div>
            </Card>
          );
        })}
        <Card padding="16px">
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#10b981",
              marginBottom: "4px"
            }}>
              {usuarios.filter(u => u.activo).length}
            </div>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Activos
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de usuarios */}
      {filteredUsuarios.length === 0 ? (
        <Card padding="60px 40px">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>👥</div>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "12px"
            }}>
              No se encontraron usuarios
            </h2>
            <p style={{
              fontSize: "16px",
              color: "#64748b"
            }}>
              Intenta ajustar los filtros de búsqueda
            </p>
          </div>
        </Card>
      ) : (
        <Card padding="0">
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse"
            }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Usuario
                  </th>
                  {/* ✅ Nueva columna: Teléfono */}
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Teléfono
                  </th>
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Rol
                  </th>
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Estado
                  </th>
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Registro
                  </th>
                  {/* ✅ Nueva columna: Último Login */}
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Último Login
                  </th>
                  <th style={{
                    padding: "16px 24px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usuario, index) => (
                  <tr 
                    key={usuario.uid}
                    style={{
                      borderBottom: "1px solid #e2e8f0",
                      backgroundColor: index % 2 === 0 ? "white" : "#f8fafc"
                    }}
                  >
                    <td style={{ padding: "16px 24px" }}>
                      <div>
                        <div style={{
                          fontSize: "15px",
                          fontWeight: "500",
                          color: "#1e293b",
                          marginBottom: "4px"
                        }}>
                          {usuario.email}
                        </div>
                        {usuario.nombre && (
                          <div style={{
                            fontSize: "13px",
                            color: "#64748b"
                          }}>
                            {usuario.nombre}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* ✅ Nueva celda: Teléfono */}
                    <td style={{ padding: "16px 24px" }}>
                      <span style={{ fontSize: "14px", color: "#334155" }}>
                        {usuario.telefono || '—'}
                      </span>
                    </td>
                    <td style={{ padding: "16px 24px" }}>
                      <select
                        value={usuario.role}
                        onChange={(e) => handleRoleChange(usuario.uid, e.target.value)}
                        style={{
                          padding: "8px 12px",
                          fontSize: "14px",
                          border: "2px solid #e2e8f0",
                          borderRadius: "6px",
                          outline: "none",
                          backgroundColor: "white",
                          cursor: "pointer",
                          fontWeight: "500",
                          color: "#334155"
                        }}
                      >
                        {ROLES_CONFIG.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "16px 24px" }}>
                      <Badge variant={usuario.activo ? "success" : "danger"}>
                        {usuario.activo ? "✓ Activo" : "✗ Inactivo"}
                      </Badge>
                    </td>
                    <td style={{ padding: "16px 24px" }}>
                      <span style={{ fontSize: "14px", color: "#64748b" }}>
                        {formatDate(usuario.fecha_registro)}
                      </span>
                    </td>
                    {/* ✅ Nueva celda: Último Login */}
                    <td style={{ padding: "16px 24px" }}>
                      <span style={{ fontSize: "14px", color: "#64748b" }}>
                        {formatDate(usuario.ultimo_login, true)}
                      </span>
                    </td>
                    <td style={{ padding: "16px 24px", textAlign: "center" }}>
                      <Button
                        variant={usuario.activo ? "danger" : "ghost"}
                        size="sm"
                        onClick={() => handleToggleStatus(usuario.uid, usuario.activo)}
                      >
                        {usuario.activo ? "Desactivar" : "Activar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}